import React, { FC, useCallback, useEffect } from 'react';

import { Stack } from '@mui/system';

import {
  AdminAllowanceNames,
  adminResetValue,
  getPharmaciesInfo,
  IAdminApproveDetailsData,
  IAdminRequestDetailsContractModel,
  ICalendarEventType,
} from '@pharmaplan/common';

import AdminLongTermHourlyRates from '../PharmacyList/AdminCreateWorkshift/AdminLongTermHourlyRates';
import LongTermHourlyRates from '../../SelfService/Workshifts/AddWorkshift/LongTermHourlyRates';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import CustomTextField from '../../common/CustomTextField';
import strings from '../../../localization';
import genericClasses from '../../../theme/GenericClasses';
import CustomCheckbox from '../../common/CustomCheckbox';
import {
  ICustomBack,
  IFromScreen,
  setBack,
  setFooter,
  setFooterShow,
} from '../../../actions/drawerActions';
import useApproveBooking from '../../../hooks/ApproveBooking/useApproveBooking';
import AllowanceWorkshift from '../../SelfService/Workshifts/AllowanceWorkshift';
import useSelfService from '../../../hooks/useSelfService';
import { Constants } from '../../../helpers/Constants';

import DetailsHeader from './DetailsHeader';
import styles from './styles';

const { priceSuffix, allowancePlaceholder } = Constants;
export interface IApproveBooking {
  workshiftId: string;
  bookingId: string;
  eventIds?: Array<string>;
  fromScreen?: IFromScreen;
  type?: ICalendarEventType;
  back?: ICustomBack;
  isAutoSyncRecord?: boolean;
  longTermShiftContractId?: string;
  professionalId?: string;
}

const ApproveBooking: FC<IApproveBooking> = ({
  bookingId,
  workshiftId,
  eventIds,
  type,
  fromScreen,
  back,
  professionalId,
  isAutoSyncRecord,
  longTermShiftContractId,
}) => {
  const dispatch = useAppDispatch();

  const { isSelfService } = useSelfService();
  const isFullSyncedEvent = !isSelfService || isAutoSyncRecord;
  const LongTermRateComponent = isSelfService
    ? LongTermHourlyRates
    : AdminLongTermHourlyRates;

  const {
    details,
    formik,
    creditsLeft,
    hourlyBonus,
    pharmacyInfoText,
    professionalInfoText,
    promoTotal,
    promoBonusTotal,
    professionalCreditsLeft,
  } = useApproveBooking({
    fromScreen,
    eventIds,
    bookingId,
    workshiftId,
    type,
    back,
    isAutoSyncRecord,
    longTermShiftContractId,
    professionalId,
  });

  const {
    pharmacyName,
    startDate,
    endDate,
    professionalName,
    pharmacistName,
    pharmacyId,
    counterOfferId,
  } = (details as IAdminApproveDetailsData) ?? {};

  const { totalWeekHours, totalWeekendHours, requests } = details as IAdminRequestDetailsContractModel;
  const userName = isSelfService || longTermShiftContractId
    ? professionalName
    : pharmacistName;
  const { handleSubmit } = formik ?? {};

  useEffect(() => {
    dispatch(
      setFooter({
        show: true,
        pagination: {
          show: false,
        },
        buttons: {
          show: true,
          primaryButton: {
            label: strings.approve,
            onClick: handleSubmit,
          },
        },
      }),
    );

    return () => {
      dispatch(setFooterShow(false));
      dispatch(setBack(null));
      dispatch(adminResetValue(['adminApproveData', 'pharmaciesInfo']));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSelfService && pharmacyId) {
      dispatch(getPharmaciesInfo({ id: pharmacyId, isAutoSyncRecord }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutoSyncRecord, isSelfService, pharmacyId]);

  const renderHourlyRates = useCallback(
    () =>
      (
        <>
          <CustomTextField
            infoText={professionalInfoText}
            disabled={!!professionalCreditsLeft}
            customClass={styles.horizontalField}
            customLabelStyle={professionalCreditsLeft ? styles.promoLabel : {}}
            suffix={priceSuffix}
            placeholder={allowancePlaceholder}
            horizontal
            id={AdminAllowanceNames.pharmacistHourlyRate}
            formik={formik}
            name={AdminAllowanceNames.pharmacistHourlyRate}
            label={
            strings.formatString(
              strings.hourlyRateFormat,
              strings.professional,
            ) as string
          }
          />

          {isFullSyncedEvent && (
          <CustomTextField
            infoText={pharmacyInfoText}
            disabled={!!creditsLeft}
            customLabelStyle={creditsLeft ? styles.promoLabel : {}}
            suffix={priceSuffix}
            customClass={styles.horizontalField}
            placeholder={allowancePlaceholder}
            horizontal
            id={AdminAllowanceNames.pharmacyHourlyRate}
            formik={formik}
            name={AdminAllowanceNames.pharmacyHourlyRate}
            label={strings.pharmacyHourlyRate}
          />
          )}
        </>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [creditsLeft, formik, isFullSyncedEvent, pharmacyInfoText],
  );

  return (
    <Stack sx={styles.container}>
      <DetailsHeader
        isCounterOffer={!!counterOfferId}
        pharmacistName={userName}
        pharmacyName={pharmacyName}
        startDate={startDate}
        endDate={endDate}
        showViewAllRequests={
          !!(longTermShiftContractId && requests?.length >= 1)
        }
      />

      {longTermShiftContractId ? (
        <LongTermRateComponent
          promoBonusTotal={promoBonusTotal}
          hourlyBonus={hourlyBonus}
          professionalCreditsLeft={professionalCreditsLeft}
          customWeekHours={totalWeekHours}
          promoTotal={promoTotal}
          customWeekendHours={totalWeekendHours}
          formik={formik}
          creditsLeft={creditsLeft}
        />
      ) : (
        <AllowanceWorkshift
          isAdmin
          formik={formik}
          pharmacyId={pharmacyId}
          renderHourlyRate={renderHourlyRates()}
          useApi={false}
        />
      )}

      {!!longTermShiftContractId && (
        <CustomTextField
          variant="outlined"
          formik={formik}
          id="allowanceNotes"
          placeholder=" "
          label={strings.allowances}
          name="allowanceNotes"
          multiline
          maxLength={200}
          customClass={genericClasses.borderNoneTextField}
        />
      )}

      <CustomTextField
        variant="outlined"
        formik={formik}
        id="notes"
        placeholder=" "
        label={strings.notes}
        name="notes"
        multiline
        maxLength={200}
        customClass={genericClasses.borderNoneTextField}
      />

      {isFullSyncedEvent && (
        <CustomTextField
          variant="outlined"
          formik={formik}
          id="notesForSalesOrder"
          placeholder=" "
          label={strings.notesForSalesOrder}
          name="notesForSalesOrder"
          multiline
          maxLength={200}
          customClass={genericClasses.borderNoneTextField}
        />
      )}

      <CustomCheckbox
        id="1"
        handleChange={(e) =>
          formik.setFieldValue('sendEmailToPharmacist', e.target.checked)}
        value={formik.values.sendEmailToPharmacist}
        name="sendEmailToPharmacist"
        labelClass={styles.checkboxLabel}
        label={
          strings.formatString(
            strings.sendEmailNotification,
            strings.professional,
          ) as string
        }
      />

      <CustomCheckbox
        value={formik.values.sendEmailToPharmacy}
        labelClass={styles.checkboxLabel}
        customClass={styles.checkbox}
        handleChange={(e) => {
          formik.setFieldValue('sendEmailToPharmacy', e.target.checked);
        }}
        id="2"
        name="sendEmailToPharmacy"
        label={
          strings.formatString(
            strings.sendEmailNotification,
            strings.pharmacies,
          ) as string
        }
      />
    </Stack>
  );
};

export default ApproveBooking;
